import React, { useEffect, useState, useCallback } from "react";
import { AppShell } from "@/components/AppShell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { api, extractErr, API_ROOT } from "@/lib/api";
import { EmptyState, Skeleton } from "@/components/EmptyState";
import { StatusBadge } from "@/components/StatusBadge";
import { PlanGate, featureAllowedByPlan } from "@/components/PlanGate";
import { useAuth } from "@/lib/auth";
import { useLocations } from "@/lib/locations";
import {
  Plus, Factory, Trash2, FileText, ArrowRight, CheckCircle2, Play, X,
  Truck as TruckIcon, MapPin, Calendar, Printer, Mail, Calculator, GitBranch, Beaker,
} from "lucide-react";
import { ProductionCostPanel } from "@/components/ProductionCostPanel";
import { Checkbox } from "@/components/ui/checkbox";

const STATUSES = ["draft", "confirmed", "in_progress", "completed", "shipped"];
const PRIORITIES = ["low", "medium", "high", "urgent"];
const SIZES = ["100ml", "300ml", "1L", "5L", "10L", "20L"];
const NEXT = { draft: "confirmed", confirmed: "in_progress", in_progress: "completed" };
const NEXT_LABEL = { draft: "Confirm", confirmed: "Start", in_progress: "Complete" };
const NEXT_ICON = { draft: CheckCircle2, confirmed: Play, in_progress: CheckCircle2 };

export default function ProductionOrders() {
  const { company } = useAuth();
  const locCtx = useLocations() || {};
  const { activeId, locationParams } = locCtx;
  const hasFeature = featureAllowedByPlan(company?.plan || "free_trial", "production");
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [filter, setFilter] = useState("all");
  const [shipFor, setShipFor] = useState(null);
  const [emailFor, setEmailFor] = useState(null);
  const [tanksFor, setTanksFor] = useState(null);
  const [allTanks, setAllTanks] = useState([]);

  const loadTanks = useCallback(async () => {
    try {
      const { data } = await api.get("/tanks", { params: locationParams });
      setAllTanks(data.tanks || []);
    } catch { /* silent */ }
  }, [locationParams]);

  const load = useCallback(async () => {
    if (!hasFeature) { setLoading(false); return; }
    setLoading(true);
    try {
      const { data } = await api.get("/production/orders", { params: locationParams });
      setOrders(data.orders || []);
    } catch (e) { toast.error(extractErr(e)); }
    setLoading(false);
  }, [hasFeature, locationParams]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { if (hasFeature) loadTanks(); }, [hasFeature, loadTanks]);

  const advance = async (o) => {
    const target = NEXT[o.status];
    if (!target) return;
    try {
      await api.put(`/production/orders/${o.id}/status`, { status: target });
      toast.success(`Order ${o.order_number} → ${target.replace("_", " ")}`);
      load();
    } catch (e) { toast.error(extractErr(e)); }
  };

  const remove = async (o) => {
    try {
      const { data } = await api.delete(`/production/orders/${o.id}`);
      toast.success(data.restored ? `Deleted — inventory restored` : `Deleted`);
      load();
    } catch (e) { toast.error(extractErr(e)); }
  };

  const downloadPdf = async (o) => {
    try {
      const token = localStorage.getItem("operon_token");
      const resp = await fetch(`${API_ROOT}/production/orders/${o.id}/pdf`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!resp.ok) throw new Error("PDF failed");
      const blob = await resp.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${o.order_number}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error("Failed to download PDF"); }
  };

  const printPdf = async (o) => {
    try {
      const token = localStorage.getItem("operon_token");
      const resp = await fetch(`${API_ROOT}/production/orders/${o.id}/pdf`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!resp.ok) throw new Error("PDF failed");
      const blob = await resp.blob();
      const url = URL.createObjectURL(blob);
      const w = window.open(url, "_blank");
      // Try to auto-print (some browsers block this; user can press Ctrl+P)
      if (w) {
        try { w.addEventListener("load", () => w.print()); } catch {}
      }
    } catch (e) { toast.error("Failed to open PDF for printing"); }
  };

  if (!hasFeature) {
    return (
      <AppShell title="Production Orders" subtitle="Create and track manufacturing orders">
        <PlanGate feature="Production Orders" planRequired="Growth"><div className="h-48" /></PlanGate>
      </AppShell>
    );
  }

  const filtered = filter === "all" ? orders : orders.filter((o) => o.status === filter);

  return (
    <AppShell
      title="Production Orders"
      subtitle={`${orders.length} total · sizes ${SIZES.join(" / ")}${activeId !== "all" ? " · scoped to active location" : ""}`}
      headerActions={<Button size="sm" onClick={() => setOpen(true)} data-testid="production-create-button"><Plus className="w-4 h-4 mr-1.5" />New order</Button>}
    >
      <div className="flex flex-wrap items-center gap-2 mb-4">
        <Button size="sm" variant={filter === "all" ? "default" : "ghost"} onClick={() => setFilter("all")} data-testid="production-filter-all">All</Button>
        {STATUSES.map((s) => (
          <Button key={s} size="sm" variant={filter === s ? "default" : "ghost"} onClick={() => setFilter(s)} data-testid={`production-filter-${s}`} className="capitalize">
            {s.replace("_", " ")} ({orders.filter((o) => o.status === s).length})
          </Button>
        ))}
      </div>

      {loading ? (
        <div className="space-y-3">{[...Array(4)].map((_, i) => <Skeleton key={i} className="h-24" />)}</div>
      ) : filtered.length === 0 ? (
        <EmptyState icon={Factory} title={filter === "all" ? "No production orders yet" : `No ${filter.replace("_", " ")} orders`}
          description="Create a multi-line order. Pick a fixed size (100ml / 300ml / 1L / 5L / 10L / 20L). When you mark it Completed, inventory is automatically deducted based on the Size BOM (bottle + cap + box per pack)."
          action={<Button onClick={() => setOpen(true)} data-testid="production-empty-create"><Plus className="w-4 h-4 mr-1.5" />Create order</Button>} />
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
          {filtered.map((o) => (
            <OrderCard
              key={o.id}
              order={o}
              advance={advance}
              remove={remove}
              downloadPdf={downloadPdf}
              printPdf={printPdf}
              onShip={() => setShipFor(o)}
              onEmailPdf={() => setEmailFor(o)}
              onManageTanks={() => setTanksFor(o)}
              tanksById={Object.fromEntries(allTanks.map((t) => [t.id, t]))}
            />
          ))}
        </div>
      )}

      {open && <OrderForm open={open} onClose={() => setOpen(false)} onCreated={() => { load(); loadTanks(); }} allTanks={allTanks} />}
      {shipFor && <ShipDialog order={shipFor} onClose={() => setShipFor(null)} onShipped={() => { setShipFor(null); load(); }} />}
      {emailFor && <EmailPdfDialog order={emailFor} onClose={() => setEmailFor(null)} />}
      {tanksFor && <ManageTanksDialog order={tanksFor} allTanks={allTanks} onClose={() => setTanksFor(null)} onSaved={() => { setTanksFor(null); load(); loadTanks(); }} />}
    </AppShell>
  );
}

function OrderCard({ order: o, advance, remove, downloadPdf, printPdf, onShip, onEmailPdf, onManageTanks, tanksById }) {
  const NxtIcon = NEXT_ICON[o.status];
  const [showCost, setShowCost] = useState(false);
  const fmt = (v) => v ? new Date(v).toLocaleString(undefined, { year: "numeric", month: "short", day: "2-digit", hour: "2-digit", minute: "2-digit" }) : "—";
  const linkedTanks = (o.tank_batch_ids || [])
    .map((tid) => tanksById?.[tid])
    .filter(Boolean);
  return (
    <div className="rounded-2xl border border-border bg-card p-4 flex flex-col" data-testid={`production-card-${o.id}`}>
      <div className="flex items-center justify-between">
        <div className="min-w-0">
          <div className="text-xs font-mono text-muted-foreground">{o.order_number}</div>
          <div className="font-heading font-semibold text-base truncate">{o.customer}</div>
        </div>
        <StatusBadge status={o.status} />
      </div>
      <div className="mt-2 flex flex-wrap items-center gap-1.5 text-[11px] text-muted-foreground">
        <span className="inline-flex rounded-full bg-muted px-2 py-0.5 capitalize">{o.priority}</span>
        <span>·</span>
        <span>{o.lines?.length || 0} line{o.lines?.length === 1 ? "" : "s"}</span>
        {o.production_date && (<><span>·</span><span className="inline-flex items-center gap-1"><Calendar className="w-3 h-3" />{new Date(o.production_date).toLocaleDateString()}</span></>)}
        {(o.tank_batch_ids || []).length > 0 && (
          <>
            <span>·</span>
            <span className="inline-flex items-center gap-1 text-primary font-medium" data-testid={`production-card-${o.id}-tanks`}>
              <Beaker className="w-3 h-3" />{(o.tank_batch_ids || []).length} tank{(o.tank_batch_ids || []).length === 1 ? "" : "s"}
            </span>
          </>
        )}
      </div>

      <div className="mt-3 space-y-1 max-h-32 overflow-y-auto">
        {(o.lines || []).slice(0, 4).map((l, idx) => (
          <div key={idx} className="text-xs flex items-center justify-between border border-border rounded px-2 py-1">
            <span className="truncate">
              {l.size && <span className="inline-flex rounded-full bg-primary/10 text-primary px-1.5 py-0.5 text-[10px] font-medium font-mono mr-1.5">{l.size}</span>}
              {l.product_name}{l.species ? ` · ${l.species}` : ""}
            </span>
            <span className="font-mono tabular-nums">x{l.quantity}</span>
          </div>
        ))}
        {(o.lines || []).length > 4 && <div className="text-[11px] text-muted-foreground">+ {o.lines.length - 4} more</div>}
      </div>

      {linkedTanks.length > 0 && (
        <div className="mt-2 rounded-lg border border-border bg-muted/20 p-2" data-testid={`production-card-${o.id}-tank-list`}>
          <div className="flex items-center gap-1 text-[10px] uppercase tracking-wide text-muted-foreground mb-1">
            <Beaker className="w-3 h-3" /> Linked tanks
          </div>
          <div className="space-y-0.5 max-h-20 overflow-y-auto">
            {linkedTanks.map((t) => (
              <div key={t.id} className="text-[11px] flex justify-between gap-1">
                <span className="truncate"><span className="font-mono mr-1">{t.batch_number}</span>{t.name}</span>
                {t.size && <span className="font-mono text-muted-foreground">{t.size}</span>}
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="mt-3 grid grid-cols-2 gap-x-3 gap-y-0.5 text-[11px] text-muted-foreground border-t border-border pt-2">
        <div className="flex justify-between"><span>Created</span><span className="font-mono">{fmt(o.created_at)}</span></div>
        {o.confirmed_at && <div className="flex justify-between"><span>Confirmed</span><span className="font-mono">{fmt(o.confirmed_at)}</span></div>}
        {o.started_at && <div className="flex justify-between"><span>Started</span><span className="font-mono">{fmt(o.started_at)}</span></div>}
        {o.completed_at && <div className="flex justify-between"><span>Completed</span><span className="font-mono">{fmt(o.completed_at)}</span></div>}
        {o.shipped_at && <div className="flex justify-between"><span>Shipped</span><span className="font-mono">{fmt(o.shipped_at)}</span></div>}
        {o.tracking_number && <div className="flex justify-between col-span-2"><span>{o.carrier || "Tracking"}</span><span className="font-mono">{o.tracking_number}</span></div>}
      </div>

      <div className="mt-3 flex flex-wrap items-center justify-end gap-1">
        <Button variant="ghost" size="sm" onClick={onManageTanks} data-testid={`production-tanks-${o.id}`}>
          <GitBranch className="w-3.5 h-3.5 mr-1" />Tanks
        </Button>
        <Button variant="ghost" size="sm" onClick={() => setShowCost((v) => !v)} data-testid={`production-cost-toggle-${o.id}`}>
          <Calculator className="w-3.5 h-3.5 mr-1" />{showCost ? "Hide cost" : "Cost"}
        </Button>
        <Button variant="ghost" size="sm" onClick={() => downloadPdf(o)} data-testid={`production-pdf-${o.id}`}><FileText className="w-3.5 h-3.5 mr-1" />PDF</Button>
        <Button variant="ghost" size="sm" onClick={() => printPdf(o)} data-testid={`production-print-${o.id}`}><Printer className="w-3.5 h-3.5 mr-1" />Print</Button>
        <Button variant="ghost" size="sm" onClick={onEmailPdf} data-testid={`production-email-${o.id}`}><Mail className="w-3.5 h-3.5 mr-1" />Email</Button>
        {NEXT[o.status] && (
          <Button size="sm" onClick={() => advance(o)} data-testid={`production-advance-${o.id}`}>
            <NxtIcon className="w-3.5 h-3.5 mr-1" />{NEXT_LABEL[o.status]}
          </Button>
        )}
        {o.status === "completed" && (
          <Button size="sm" variant="default" onClick={onShip} data-testid={`production-ship-${o.id}`}>
            <TruckIcon className="w-3.5 h-3.5 mr-1" />Ship
          </Button>
        )}
        <AlertDialog>
          <AlertDialogTrigger asChild>
            <Button variant="ghost" size="icon" className="h-8 w-8 text-destructive" data-testid={`production-delete-${o.id}`}><Trash2 className="w-3.5 h-3.5" /></Button>
          </AlertDialogTrigger>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Delete order?</AlertDialogTitle>
              <AlertDialogDescription>If inventory was deducted for this order, it will be automatically restored.</AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction className="bg-destructive text-destructive-foreground hover:bg-destructive/90" onClick={() => remove(o)}>Delete</AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>

      {showCost && (
        <div className="mt-3" data-testid={`production-cost-wrap-${o.id}`}>
          <ProductionCostPanel orderId={o.id} status={o.status} />
        </div>
      )}
    </div>
  );
}

function OrderForm({ open, onClose, onCreated, allTanks = [] }) {
  const locCtx = useLocations() || {};
  const { locations = [], activeId } = locCtx;
  const [products, setProducts] = useState([]);
  const [form, setForm] = useState({
    customer: "",
    location_id: activeId !== "all" ? activeId : (locations.find((l) => l.is_default)?.id || ""),
    priority: "medium",
    production_date: "",
    notes: "",
    lines: [{ product_id: "", quantity: 1, species: "", size: "1L", size_bom_id: "", notes: "" }],
    tank_batch_ids: [],
  });
  const [submitting, setSubmitting] = useState(false);
  // Phase 9: load all Size BOM variants so the per-line picker is fast
  const [sizeBomsBySize, setSizeBomsBySize] = useState({});

  useEffect(() => {
    api.get("/inventory?category=Finished Goods&limit=2000").then(({ data }) => setProducts(data.items || []))
      .catch(() => api.get("/inventory?limit=2000").then(({ data }) => setProducts(data.items || [])));
    api.get("/size-bom").then(({ data }) => {
      const map = {};
      (data.size_boms || []).forEach((b) => {
        if (!map[b.size]) map[b.size] = [];
        map[b.size].push(b);
      });
      Object.keys(map).forEach((sz) => {
        map[sz].sort((a, b) => {
          if (a.is_default !== b.is_default) return a.is_default ? -1 : 1;
          return (a.name || "").localeCompare(b.name || "");
        });
      });
      setSizeBomsBySize(map);
    }).catch(() => {});
  }, []);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const updateLine = (i, k, v) => setForm((f) => {
    const lines = [...f.lines];
    lines[i] = { ...lines[i], [k]: v };
    // When the size changes, clear the variant pick so the size's default is used
    if (k === "size") lines[i].size_bom_id = "";
    return { ...f, lines };
  });
  const addLine = () => setForm((f) => ({ ...f, lines: [...f.lines, { product_id: "", quantity: 1, species: "", size: "1L", size_bom_id: "", notes: "" }] }));
  const removeLine = (i) => setForm((f) => ({ ...f, lines: f.lines.filter((_, idx) => idx !== i) }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.customer.trim()) { toast.error("Customer name is required"); return; }
    if (form.lines.some((l) => !l.product_id || Number(l.quantity) <= 0)) { toast.error("Every line needs product & qty > 0"); return; }
    if (form.lines.some((l) => !l.size || !SIZES.includes(l.size))) { toast.error("Every line needs a valid size"); return; }
    setSubmitting(true);
    try {
      const payload = {
        ...form,
        location_id: form.location_id || null,
        production_date: form.production_date ? new Date(form.production_date).toISOString() : null,
        lines: form.lines.map((l) => ({
          ...l,
          quantity: Number(l.quantity),
          size_bom_id: l.size_bom_id || null,  // null = use the size's default variant
        })),
        tank_batch_ids: (form.tank_batch_ids || []).filter(Boolean),
      };
      await api.post("/production/orders", payload);
      toast.success("Order created");
      onClose(); onCreated();
    } catch (e) { toast.error(extractErr(e)); }
    setSubmitting(false);
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>New production order</DialogTitle>
          <DialogDescription>
            Enter the customer name and add as many size+quantity lines as you need. Inventory packaging (bottle, cap, box) is auto-deducted from the Size BOM when you mark the order <b>Completed</b>.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div className="space-y-1.5 md:col-span-2">
              <Label>Customer name *</Label>
              <Input required value={form.customer} onChange={(e) => update("customer", e.target.value)} placeholder="Big Farm Inc." data-testid="production-form-customer" />
            </div>
            <div className="space-y-1.5">
              <Label>Priority</Label>
              <Select value={form.priority} onValueChange={(v) => update("priority", v)}>
                <SelectTrigger data-testid="production-form-priority"><SelectValue /></SelectTrigger>
                <SelectContent>{PRIORITIES.map((p) => <SelectItem key={p} value={p} className="capitalize">{p}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Production date</Label>
              <Input type="date" value={form.production_date} onChange={(e) => update("production_date", e.target.value)} />
            </div>
            {locations.length > 1 && (
              <div className="space-y-1.5 md:col-span-2">
                <Label className="flex items-center gap-1"><MapPin className="w-3.5 h-3.5" /> Location</Label>
                <Select value={form.location_id || "auto"} onValueChange={(v) => update("location_id", v === "auto" ? "" : v)}>
                  <SelectTrigger data-testid="production-form-location"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="auto">Default location</SelectItem>
                    {locations.map((l) => <SelectItem key={l.id} value={l.id}>{l.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            )}
            <div className="space-y-1.5 md:col-span-3">
              <Label>Notes</Label>
              <Textarea value={form.notes} onChange={(e) => update("notes", e.target.value)} rows={2} />
            </div>
          </div>

          <div>
            <div className="flex items-center justify-between">
              <Label>Products (multi-line)</Label>
              <Button type="button" variant="ghost" size="sm" onClick={addLine} data-testid="production-form-add-line"><Plus className="w-4 h-4 mr-1" />Add line</Button>
            </div>
            <div className="mt-2 space-y-2">
              {form.lines.map((l, i) => (
                <div key={i} className="grid grid-cols-12 gap-2 items-end rounded-lg border border-border p-2">
                  <div className="col-span-12 md:col-span-4 space-y-1">
                    <Label className="text-[11px]">Product</Label>
                    <Select value={l.product_id} onValueChange={(v) => updateLine(i, "product_id", v)}>
                      <SelectTrigger data-testid={`production-form-line-${i}-product`}><SelectValue placeholder="Select product" /></SelectTrigger>
                      <SelectContent className="max-h-[300px]">
                        {products.map((p) => (
                          <SelectItem key={p.id} value={p.id}><span className="font-mono text-xs mr-2">{p.sku}</span>{p.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="col-span-6 md:col-span-2 space-y-1">
                    <Label className="text-[11px]">Size</Label>
                    <Select value={l.size} onValueChange={(v) => updateLine(i, "size", v)}>
                      <SelectTrigger data-testid={`production-form-line-${i}-size`}><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {SIZES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="col-span-6 md:col-span-2 space-y-1">
                    <Label className="text-[11px]">Quantity</Label>
                    <Input type="number" min="0" value={l.quantity} onChange={(e) => updateLine(i, "quantity", e.target.value)} data-testid={`production-form-line-${i}-qty`} />
                  </div>
                  <div className="col-span-12 md:col-span-3 space-y-1">
                    <Label className="text-[11px]">Packaging variant</Label>
                    <Select
                      value={l.size_bom_id || "default"}
                      onValueChange={(v) => updateLine(i, "size_bom_id", v === "default" ? "" : v)}
                    >
                      <SelectTrigger data-testid={`production-form-line-${i}-sizebom`}>
                        <SelectValue placeholder="Default" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="default">
                          <span className="text-muted-foreground">Use {l.size} default</span>
                        </SelectItem>
                        {(sizeBomsBySize[l.size] || []).map((sb) => (
                          <SelectItem key={sb.id} value={sb.id}>
                            {sb.name}{sb.is_default ? " (default)" : ""}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="col-span-10 md:col-span-3 space-y-1">
                    <Label className="text-[11px]">Species (optional)</Label>
                    <Input value={l.species} onChange={(e) => updateLine(i, "species", e.target.value)} placeholder="Calf / Horse…" />
                  </div>
                  <div className="col-span-2 md:col-span-12 flex justify-end">
                    {form.lines.length > 1 && (
                      <Button type="button" variant="ghost" size="icon" onClick={() => removeLine(i)}><X className="w-4 h-4" /></Button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div>
            <Label className="flex items-center gap-1.5">
              <Beaker className="w-3.5 h-3.5 text-primary" />
              Linked tank batches (optional)
            </Label>
            <p className="text-[11px] text-muted-foreground mt-0.5 mb-2">
              Tag the tanks that fed this order — full lot/batch traceability is then visible from each tank's detail page.
            </p>
            {allTanks.length === 0 ? (
              <div className="text-[11px] text-muted-foreground italic">No tanks created yet.</div>
            ) : (
              <div className="rounded-lg border border-border bg-muted/20 p-2 max-h-40 overflow-y-auto">
                {allTanks.map((t) => {
                  const checked = (form.tank_batch_ids || []).includes(t.id);
                  return (
                    <label
                      key={t.id}
                      className="flex items-center gap-2 py-1 px-1 rounded hover:bg-muted/40 cursor-pointer text-xs"
                      data-testid={`production-form-tank-${t.id}`}
                    >
                      <Checkbox
                        checked={checked}
                        onCheckedChange={(c) =>
                          update(
                            "tank_batch_ids",
                            c
                              ? [...(form.tank_batch_ids || []), t.id]
                              : (form.tank_batch_ids || []).filter((x) => x !== t.id)
                          )
                        }
                      />
                      <span className="font-mono text-[10px] text-muted-foreground w-24 shrink-0">{t.batch_number}</span>
                      <span className="font-medium truncate flex-1">{t.name}</span>
                      {t.size && <span className="font-mono text-[10px] bg-primary/10 text-primary px-1.5 py-0.5 rounded-full">{t.size}</span>}
                    </label>
                  );
                })}
              </div>
            )}
          </div>

          <DialogFooter>
            <Button type="button" variant="ghost" onClick={onClose}>Cancel</Button>
            <Button type="submit" disabled={submitting} data-testid="production-form-submit"><ArrowRight className="w-4 h-4 mr-1.5" />{submitting ? "Creating…" : "Create order"}</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function ManageTanksDialog({ order, allTanks = [], onClose, onSaved }) {
  const [selected, setSelected] = useState(new Set(order.tank_batch_ids || []));
  const [submitting, setSubmitting] = useState(false);

  const toggle = (id, on) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (on) next.add(id); else next.delete(id);
      return next;
    });
  };

  const save = async () => {
    setSubmitting(true);
    try {
      await api.put(`/production/orders/${order.id}/tanks`, {
        tank_batch_ids: Array.from(selected),
      });
      toast.success(`Tank links updated for ${order.order_number}`);
      onSaved();
    } catch (e) { toast.error(extractErr(e, "Failed to update tank links")); }
    setSubmitting(false);
  };

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto" data-testid="manage-tanks-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <GitBranch className="w-5 h-5 text-primary" />
            Manage linked tanks
          </DialogTitle>
          <DialogDescription>
            Order <span className="font-mono">{order.order_number}</span> · {order.customer}
            <br />
            Pick the tanks that fed this order to enable lot/batch traceability.
          </DialogDescription>
        </DialogHeader>

        {allTanks.length === 0 ? (
          <div className="text-sm text-muted-foreground py-6 text-center">
            No tanks created yet. Create a tank batch first.
          </div>
        ) : (
          <div className="space-y-1 max-h-[55vh] overflow-y-auto rounded-lg border border-border bg-muted/20 p-2">
            {allTanks.map((t) => {
              const checked = selected.has(t.id);
              return (
                <label
                  key={t.id}
                  className="flex items-center gap-3 py-1.5 px-2 rounded hover:bg-muted/40 cursor-pointer"
                  data-testid={`manage-tank-${t.id}`}
                >
                  <Checkbox checked={checked} onCheckedChange={(c) => toggle(t.id, !!c)} />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="font-mono text-[11px] text-muted-foreground">{t.batch_number}</span>
                      {t.size && <span className="font-mono text-[10px] bg-primary/10 text-primary px-1.5 py-0.5 rounded-full">{t.size}</span>}
                    </div>
                    <div className="font-medium text-sm truncate">{t.name}</div>
                    <div className="text-[11px] text-muted-foreground">
                      {(t.materials || []).length} materials · €{Number(t.total_cost || 0).toFixed(2)}
                      {(t.linked_orders_count || 0) > 0 && (
                        <span className="ml-2">· also linked to {t.linked_orders_count} other order{t.linked_orders_count === 1 ? "" : "s"}</span>
                      )}
                    </div>
                  </div>
                </label>
              );
            })}
          </div>
        )}

        <DialogFooter>
          <Button type="button" variant="ghost" onClick={onClose}>Cancel</Button>
          <Button type="button" onClick={save} disabled={submitting} data-testid="manage-tanks-save">
            {submitting ? "Saving…" : `Save links (${selected.size})`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ShipDialog({ order, onClose, onShipped }) {
  const [carrier, setCarrier] = useState("");
  const [tracking, setTracking] = useState("");
  const [notes, setNotes] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.post(`/production/orders/${order.id}/ship`, {
        carrier: carrier || null,
        tracking_number: tracking || null,
        notes: notes || null,
      });
      toast.success(`Order ${order.order_number} shipped`);
      onShipped();
    } catch (e) { toast.error(extractErr(e)); }
    setSubmitting(false);
  };

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Mark order as shipped</DialogTitle>
          <DialogDescription>
            Records carrier, tracking number, and shipped timestamp on order <span className="font-mono">{order.order_number}</span>.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <div className="space-y-1.5">
            <Label>Carrier</Label>
            <Input value={carrier} onChange={(e) => setCarrier(e.target.value)} placeholder="DHL / UPS / FedEx / Local" data-testid="ship-form-carrier" />
          </div>
          <div className="space-y-1.5">
            <Label>Tracking number</Label>
            <Input value={tracking} onChange={(e) => setTracking(e.target.value)} placeholder="e.g. 1Z999AA1012345670" data-testid="ship-form-tracking" />
          </div>
          <div className="space-y-1.5">
            <Label>Shipping notes</Label>
            <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} placeholder="e.g. Pallet 1 of 2, partial dispatch" />
          </div>
          <DialogFooter>
            <Button type="button" variant="ghost" onClick={onClose}>Cancel</Button>
            <Button type="submit" disabled={submitting} data-testid="ship-form-submit">
              <TruckIcon className="w-4 h-4 mr-1.5" />{submitting ? "Shipping…" : "Confirm shipment"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function EmailPdfDialog({ order, onClose }) {
  const [to, setTo] = useState("");
  const [subject, setSubject] = useState(`Production order ${order.order_number}`);
  const [message, setMessage] = useState("");
  const [sending, setSending] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!to.trim()) { toast.error("Enter at least one recipient email"); return; }
    setSending(true);
    try {
      const { data } = await api.post(`/production/orders/${order.id}/email-pdf`, {
        to: to.split(",").map((s) => s.trim()).filter(Boolean),
        subject, message,
      });
      if (data.sent > 0) toast.success(`PDF emailed to ${data.sent} recipient${data.sent === 1 ? "" : "s"}`);
      else toast.error("No emails delivered — check Resend configuration");
      onClose();
    } catch (e) { toast.error(extractErr(e, "Failed to send email")); }
    setSending(false);
  };

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Email production order PDF</DialogTitle>
          <DialogDescription>
            Send the PDF for order <span className="font-mono">{order.order_number}</span> as an email attachment. Separate multiple recipients with commas.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <div className="space-y-1.5">
            <Label>To *</Label>
            <Input type="text" required value={to} onChange={(e) => setTo(e.target.value)} placeholder="customer@example.com, ops@example.com" data-testid="email-pdf-to" />
          </div>
          <div className="space-y-1.5">
            <Label>Subject</Label>
            <Input value={subject} onChange={(e) => setSubject(e.target.value)} data-testid="email-pdf-subject" />
          </div>
          <div className="space-y-1.5">
            <Label>Message (optional)</Label>
            <Textarea value={message} onChange={(e) => setMessage(e.target.value)} rows={3} placeholder="Hi, please find the production order attached…" data-testid="email-pdf-message" />
          </div>
          <DialogFooter>
            <Button type="button" variant="ghost" onClick={onClose}>Cancel</Button>
            <Button type="submit" disabled={sending} data-testid="email-pdf-submit">
              <Mail className="w-4 h-4 mr-1.5" />{sending ? "Sending…" : "Send email"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
